/**
 * Cicd — CI/CD Pipeline & historique
 * Gauche: vue pipeline (build→test→deploy stages)
 * Droite: table d'historique avec filtres (projet, statut)
 * Drawer de détail pour l'événement sélectionné
 */

import { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";

type EventStatus = "running" | "success" | "failed" | "queued" | "cancelled";

interface PipelineStage {
  name: string;
  icon: string;
  status: EventStatus;
  pct?: number;
  duration?: string;
}

interface CicdEvent {
  id: string;
  project: string;
  branch: string;
  commit: string;
  commitMessage: string;
  author: string;
  status: EventStatus;
  triggeredAt: string;
  duration?: string;
  stages: PipelineStage[];
  environment?: string;
}

const STAGE_ORDER = ["checkout", "build", "test", "scan", "deploy"];
const STAGE_ICONS: Record<string, string> = {
  checkout: "📦",
  build: "🔨",
  test: "🧪",
  scan: "🔍",
  deploy: "🚀",
};

const STATUS_META: Record<EventStatus, { label: string; color: string; stageClass: string }> = {
  running: { label: "En cours", color: "var(--amber)", stageClass: "active" },
  success: { label: "Réussi", color: "var(--olive)", stageClass: "done" },
  failed: { label: "Échec", color: "var(--rust)", stageClass: "failed" },
  queued: { label: "En file", color: "var(--blue)", stageClass: "pending" },
  cancelled: { label: "Annulé", color: "var(--muted)", stageClass: "pending" },
};

export default function Cicd() {
  const [events, setEvents] = useState<CicdEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Filters
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    try {
      setLoading(true);
      const data = await api.cicd.list();
      setEvents((data ?? []) as unknown as CicdEvent[]);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const projects = useMemo(() => {
    return Array.from(new Set(events.map((e) => e.project))).sort();
  }, [events]);

  const filtered = useMemo(() => {
    return events.filter((e) => {
      if (projectFilter !== "all" && e.project !== projectFilter) return false;
      if (statusFilter !== "all" && e.status !== statusFilter) return false;
      return true;
    });
  }, [events, projectFilter, statusFilter]);

  const selected = events.find((e) => e.id === selectedId) || null;

  const stats = {
    total: events.length,
    running: events.filter((e) => e.status === "running").length,
    success: events.filter((e) => e.status === "success").length,
    failed: events.filter((e) => e.status === "failed").length,
  };
  const successRate = stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0;

  return (
    <div className="page active" style={{ display: "grid", gap: "14px" }}>
      <div className="page-header">
        <div>
          <div className="eyebrow">delivery</div>
          <h1>CI/CD</h1>
          <p>
            {stats.total} pipeline{stats.total > 1 ? "s" : ""} · {stats.running} en cours ·{" "}
            taux de réussite {successRate}%
          </p>
        </div>
        <div className="header-actions">
          <button className="btn" onClick={load}>↻ Actualiser</button>
        </div>
      </div>

      {error && (
        <div className="ops-card" style={{ borderColor: "var(--rust)" }}>
          <p style={{ color: "var(--rust)" }}>⚠ {error}</p>
        </div>
      )}

      {loading && <div className="no-results">Chargement des pipelines…</div>}

      {/* Stats */}
      {!loading && !error && (
        <div className="stats-row" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
          <div className="stat-card">
            <div className="eyebrow">total</div>
            <div className="val">{stats.total}</div>
            <div className="sub">exécutions</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">en cours</div>
            <div className="val" style={{ color: "var(--amber)" }}>{stats.running}</div>
            <div className="sub">running</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">réussis</div>
            <div className="val" style={{ color: "var(--olive)" }}>{stats.success}</div>
            <div className="sub">success</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">échoués</div>
            <div className="val" style={{ color: "var(--rust)" }}>{stats.failed}</div>
            <div className="sub">failed</div>
          </div>
          <div className="stat-card">
            <div className="eyebrow">taux succès</div>
            <div className="val" style={{ color: successRate >= 80 ? "var(--olive)" : "var(--amber)" }}>{successRate}%</div>
            <div className="sub">sur 30 jours</div>
          </div>
        </div>
      )}

      {!loading && !error && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px", alignItems: "start" }}>
          {/* Left: Active pipeline view */}
          <div style={{ display: "grid", gap: "12px" }}>
            <div className="eyebrow">pipeline actif</div>
            {filtered.filter((e) => e.status === "running").length > 0 ? (
              filtered
                .filter((e) => e.status === "running")
                .map((event) => <PipelineCard key={event.id} event={event} onSelect={() => setSelectedId(event.id)} />)
            ) : (
              <div className="ops-card">
                <p style={{ color: "var(--muted)", fontSize: "13px" }}>
                  ⏸ Aucun pipeline en cours d'exécution.
                </p>
              </div>
            )}

            {/* Deploy matrix */}
            <div className="deploy-matrix">
              {["dev", "stage", "prod"].map((env) => {
                const envEvents = events.filter((e) => e.environment === env && e.status === "success");
                const latest = envEvents[0];
                return (
                  <div key={env} className={`deploy-env ${env}`}>
                    <div className="deploy-env-name">{env}</div>
                    <div className="deploy-env-ver">
                      {latest ? latest.commit.slice(0, 7) : "—"}
                    </div>
                    <div className="deploy-env-meta">
                      {latest ? `${latest.project}` : "non déployé"}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: History */}
          <div style={{ display: "grid", gap: "12px" }}>
            <div className="eyebrow">historique</div>

            {/* Filters */}
            <div className="ops-card" style={{ padding: "12px 14px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div className="form-group" style={{ gap: "4px" }}>
                  <label>Projet</label>
                  <select className="form-select" value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                    <option value="all">Tous</option>
                    {projects.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group" style={{ gap: "4px" }}>
                  <label>Statut</label>
                  <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">Tous</option>
                    <option value="running">En cours</option>
                    <option value="success">Réussi</option>
                    <option value="failed">Échec</option>
                    <option value="queued">En file</option>
                    <option value="cancelled">Annulé</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="ops-card" style={{ padding: 0, overflow: "hidden" }}>
              {filtered.length === 0 ? (
                <div className="no-results">Aucun événement.</div>
              ) : (
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th>Projet</th>
                      <th>Branche</th>
                      <th>Statut</th>
                      <th>Durée</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, 20).map((event) => {
                      const meta = STATUS_META[event.status];
                      return (
                        <tr
                          key={event.id}
                          onClick={() => setSelectedId(event.id)}
                          style={{ cursor: "pointer" }}
                        >
                          <td style={{ fontWeight: 600 }}>{event.project}</td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: "11px" }}>{event.branch}</td>
                          <td>
                            <span style={{ color: meta.color, fontWeight: 600, fontSize: "11px" }}>
                              {meta.label}
                            </span>
                          </td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
                            {event.duration || "—"}
                          </td>
                          <td style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
                            {new Date(event.triggeredAt).toLocaleString("fr-FR")}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail Drawer */}
      {selected && (
        <CicdDrawer event={selected} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}

// ── Pipeline Card (active pipeline view) ─────────────────────────

function PipelineCard({ event, onSelect }: { event: CicdEvent; onSelect: () => void }) {
  return (
    <div className="pipeline-card" onClick={onSelect} style={{ cursor: "pointer" }}>
      <div className="pipeline-head">
        <span className="pipeline-name">{event.project}</span>
        <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
          {event.branch}
        </span>
        <span style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--blue)" }}>
          {event.commit.slice(0, 7)}
        </span>
      </div>
      <div style={{ fontSize: "13px", color: "var(--muted)", lineHeight: 1.4 }}>
        {event.commitMessage}
      </div>
      <div className="pipeline-stages">
        <div className="pipeline-row">
          {STAGE_ORDER.map((stageName, i) => {
            const stage = event.stages.find((s) => s.name === stageName);
            if (!stage) return null;
            const meta = STATUS_META[stage.status];
            return (
              <div key={stage.name} style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <div className={`pipeline-stage ${meta.stageClass}`}>
                  <div className="pipeline-stage-icon">{STAGE_ICONS[stage.name] || "⚙"}</div>
                  <div className="pipeline-stage-name">{stage.name}</div>
                  <div className="pipeline-stage-pct" style={{ color: meta.color }}>
                    {stage.status === "running" && stage.pct ? `${stage.pct}%` : stage.status === "success" ? "✓" : stage.status === "failed" ? "✗" : "—"}
                  </div>
                </div>
                {i < STAGE_ORDER.length - 1 && <div className={`pipeline-connector ${stage.status === "success" ? "done" : ""}`} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Detail Drawer ────────────────────────────────────────────────

function CicdDrawer({ event, onClose }: { event: CicdEvent; onClose: () => void }) {
  const meta = STATUS_META[event.status];
  return (
    <div className="side-drawer open">
      <div className="side-drawer-header">
        <div>
          <strong style={{ fontSize: "16px" }}>{event.project}</strong>
          <div style={{ fontFamily: "var(--mono)", fontSize: "11px", color: "var(--muted)" }}>
            {event.branch} · {event.commit.slice(0, 7)}
          </div>
        </div>
        <button className="modal-close" onClick={onClose}>×</button>
      </div>
      <div className="side-drawer-body" style={{ display: "grid", gap: "16px" }}>
        <div className="side-card">
          <div className="side-row">
            <span>Statut</span>
            <strong style={{ color: meta.color }}>{meta.label}</strong>
          </div>
          <div className="side-row">
            <span>Commit</span>
            <strong style={{ fontFamily: "var(--mono)", fontSize: "12px" }}>{event.commit}</strong>
          </div>
          <div className="side-row">
            <span>Auteur</span>
            <strong>{event.author}</strong>
          </div>
          <div className="side-row">
            <span>Branche</span>
            <strong>{event.branch}</strong>
          </div>
          <div className="side-row">
            <span>Durée</span>
            <strong>{event.duration || "—"}</strong>
          </div>
          <div className="side-row">
            <span>Déclenché</span>
            <strong style={{ fontFamily: "var(--mono)", fontSize: "11px" }}>
              {new Date(event.triggeredAt).toLocaleString("fr-FR")}
            </strong>
          </div>
        </div>

        <div style={{ display: "grid", gap: "8px" }}>
          <div className="eyebrow">message de commit</div>
          <div style={{ padding: "12px", borderRadius: "12px", background: "rgba(0,0,0,0.14)", fontSize: "13px", lineHeight: 1.5 }}>
            {event.commitMessage}
          </div>
        </div>

        <div style={{ display: "grid", gap: "8px" }}>
          <div className="eyebrow">étapes</div>
          {event.stages.map((stage) => {
            const sMeta = STATUS_META[stage.status];
            return (
              <div key={stage.name} className={`pipeline-stage ${sMeta.stageClass}`} style={{ flexDirection: "row", justifyContent: "space-between", minWidth: "auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "16px" }}>{STAGE_ICONS[stage.name] || "⚙"}</span>
                  <span style={{ fontSize: "13px", fontWeight: 600 }}>{stage.name}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {stage.duration && (
                    <span style={{ fontFamily: "var(--mono)", fontSize: "10px", color: "var(--muted)" }}>{stage.duration}</span>
                  )}
                  <span style={{ color: sMeta.color, fontSize: "12px", fontWeight: 600 }}>
                    {stage.status === "running" && stage.pct ? `${stage.pct}%` : sMeta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
